import Flag from "components/common/Flag";
import Icon from "components/Icons/Icon";
import { motion } from "framer-motion";
import { buildLocalizedPath, hasLocalisedRoute, MARKETING_LANG_CODES } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const LANG_COUNTRY: Record<string, string> = {
	en: "GB",
	nl: "NL",
	de: "DE",
	fr: "FR",
	es: "ES",
	it: "IT",
	pt: "PT",
};
const LANG_NAMES: Record<string, string> = {
	en: "English",
	nl: "Nederlands",
	de: "Deutsch",
	fr: "Français",
	es: "Español",
	it: "Italiano",
	pt: "Português",
};
const ALL_LANGS: string[] = [...MARKETING_LANG_CODES];

interface LanguageSwitcherProps {
	localeSlugs?: Record<string, string>; // locale→slug, only for article pages
	type: "list" | "article" | "page";
	variant?: "dropdown" | "grid"; // "grid" renders every option inline (used in the mobile nav menu)
}

/**
 * `buildUrl` takes the pathname rather than reading `window`, because this component renders
 * during SSR too and `window` is not there.
 *
 * It used to read `window.location.pathname` directly with a `typeof window === "undefined"`
 * guard falling back to `"/"`. On a server-rendered page that fallback is what shipped: every
 * `type="page"` switcher emitted `/nl/`, `/de/`, … — links to the *homepage* — and hydration did
 * not correct them, because the markup matched and nothing re-rendered the anchors. Live on
 * /about and /contact before this was fixed; the only reason it looked fine anywhere is that
 * `type="list"`/`"article"` never touch the path, and a page that 404s at the SSR layer (a
 * not-found hub) renders client-side where `window` does exist.
 *
 * `useLocation()` is correct on both sides: the server wraps the tree in `StaticRouter` with the
 * real pathname (`entry-server.tsx:427`), and `Navigation` — this component's only parent — is
 * already inside the router.
 */
function buildUrl(lang: string, type: "list" | "article" | "page", pathname: string, localeSlugs?: Record<string, string>): string {
	if (type === "page") {
		// A visitor on /itinerary-planner who picks Dutch should land on a Dutch page — and since
		// phase 3 step 1 of work/locale-coverage-unification.md, they do: the 14 feature pages are
		// in `hasLocalisedRoute` now (see its doc comment in `i18n/urlLocale.ts`), so this builds
		// the real locale-prefixed URL for them the same as any other localised page.
		//
		// The fallback below still matters for everything else `hasLocalisedRoute` says no to
		// (/about, /pricing, ...): the locale's homepage is a worse answer than a translated page
		// and a much better one than the blank, indexable SPA shell an unrouted locale URL used to
		// serve — that shell is how Google found and indexed ~114 of these in the first place.
		const target = hasLocalisedRoute(pathname) ? pathname : "/";
		return buildLocalizedPath(target, lang as (typeof MARKETING_LANG_CODES)[number]);
	}
	const prefix = lang === "en" ? "" : `/${lang}`;
	return type === "list" ? `${prefix}/blog` : `${prefix}/blog/${localeSlugs?.[lang] ?? ""}`;
}

const LanguageSwitcher = ({ localeSlugs, type, variant = "dropdown" }: LanguageSwitcherProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { pathname } = useLocation();
	// The page's locale as `MarketingShell` provides it, else the router location's prefix (the same
	// pathname read above). Not the pathname alone: a page rendered outside `MarketingShell` under a
	// `MarketingLangContext` of its own (`TripNotFound`, on a URL with no locale prefix) would show "EN".
	const currentLang = useMarketingLang();
	const ref = useRef<HTMLDivElement>(null);

	const availableLangs = type === "article" ? ALL_LANGS.filter((l) => localeSlugs?.[l]) : ALL_LANGS;

	useEffect(() => {
		if (!isOpen) return undefined;
		const onClick = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
		};
		document.addEventListener("mousedown", onClick);
		return () => document.removeEventListener("mousedown", onClick);
	}, [isOpen]);

	if (availableLangs.length <= 1) return null;

	if (variant === "grid") {
		return (
			<div className="lang-switcher lang-switcher--grid">
				{availableLangs.map((lang) =>
					lang === currentLang ? (
						<span key={lang} className="lang-switcher__grid-item lang-switcher__grid-item--active">
							<Flag country={LANG_COUNTRY[lang]} rounded width={16} height={16} />
							{lang.toUpperCase()}
						</span>
					) : (
						<a key={lang} href={buildUrl(lang, type, pathname, localeSlugs)} className="lang-switcher__grid-item">
							<Flag country={LANG_COUNTRY[lang]} rounded width={16} height={16} />
							{lang.toUpperCase()}
						</a>
					)
				)}
			</div>
		);
	}

	return (
		<motion.div ref={ref} initial={false} animate={isOpen ? "open" : "closed"} className="dropdown-menu lang-switcher">
			<motion.div whileTap={{ scale: 0.97 }} onClick={() => setIsOpen((open) => !open)} className="dropdown-menu__label">
				<span className="lang-switcher__flag-badge">
					<Flag country={LANG_COUNTRY[currentLang]} rounded width={20} height={20} />
				</span>
				{currentLang.toUpperCase()}
				<motion.div
					variants={{
						open: { rotate: 180 },
						closed: { rotate: 0 },
					}}
					transition={{ duration: 0.2 }}
					style={{ originY: 0.48, originX: 0.68, display: "flex", height: 8, width: 8, alignItems: "center", justifyContent: "center" }}
				>
					<Icon name="chevron-down" size={8} />
				</motion.div>
			</motion.div>

			<motion.ul
				className="lang-switcher__dropdown"
				variants={{
					open: {
						clipPath: "inset(-2rem -2rem -3rem -2rem)",
						transition: { type: "spring", bounce: 0, duration: 0.4 },
					},
					closed: {
						clipPath: "inset(10% 50% 90% 50%)",
						transition: { type: "spring", bounce: 0, duration: 0.3 },
					},
				}}
				style={{ pointerEvents: isOpen ? "auto" : "none" }}
			>
				{availableLangs.map((lang) => (
					<li key={lang}>
						{lang === currentLang ? (
							<span className="lang-switcher__item lang-switcher__item--active">
								<span className="lang-switcher__flag-badge lang-switcher__flag-badge--item">
									<Flag country={LANG_COUNTRY[lang]} rounded width={22} height={22} />
								</span>
								{LANG_NAMES[lang]}
							</span>
						) : (
							<a href={buildUrl(lang, type, pathname, localeSlugs)} className="lang-switcher__item">
								<span className="lang-switcher__flag-badge lang-switcher__flag-badge--item">
									<Flag country={LANG_COUNTRY[lang]} rounded width={22} height={22} />
								</span>
								{LANG_NAMES[lang]}
							</a>
						)}
					</li>
				))}
			</motion.ul>
		</motion.div>
	);
};

export default LanguageSwitcher;
